<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Button from 'dashboard/components-next/button/Button.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import AiAPI from 'custom/api/ai';
import { useLocoI18n } from 'custom/composables/useLocoI18n';
import { useAiSettings } from './useAiSettings';

// AI assistants: who answers which inboxes, and a way to create a new one
const { t } = useLocoI18n();
const route = useRoute();
const router = useRouter();
const inboxes = useMapGetter('inboxes/getInboxes');
const { settings, isConfigured, load } = useAiSettings();

const assistants = ref(null);
const dialogRef = ref(null);
const draft = ref({ name: '', description: '' });
const isCreating = ref(false);

const inboxNames = computed(() =>
  Object.fromEntries(inboxes.value.map(inbox => [inbox.id, inbox.name]))
);

const openAssistant = id =>
  router.push({
    name: 'loco_ai_assistant',
    params: { accountId: route.params.accountId, assistantId: id },
  });

const openSettings = () =>
  router.push({
    name: 'loco_ai_settings',
    params: { accountId: route.params.accountId },
  });

const create = async () => {
  if (!draft.value.name.trim()) return;
  isCreating.value = true;
  try {
    const { data } = await AiAPI.createAssistant(draft.value);
    dialogRef.value.close();
    openAssistant(data.id);
  } catch (error) {
    useAlert(error.response?.data?.error || error.message);
  } finally {
    isCreating.value = false;
  }
};

const openDialog = () => {
  draft.value = { name: '', description: '' };
  dialogRef.value.open();
};

onMounted(async () => {
  load();
  const { data } = await AiAPI.assistants();
  assistants.value = data.payload;
});
</script>

<template>
  <div class="w-full h-full overflow-y-auto bg-n-surface-1">
    <div class="max-w-3xl mx-auto px-6 py-8 flex flex-col gap-8">
      <header class="flex flex-wrap items-start gap-4">
        <div class="flex flex-col gap-1 flex-1 min-w-0">
          <h1 class="text-heading-1 text-n-slate-12">
            {{ t('LOCO.AI.ASSISTANTS.TITLE') }}
          </h1>
          <p class="text-sm text-n-slate-11">
            {{ t('LOCO.AI.ASSISTANTS.SUBTITLE') }}
          </p>
        </div>
        <Button
          sm
          icon="i-lucide-plus"
          :label="t('LOCO.AI.ASSISTANTS.NEW')"
          @click="openDialog"
        />
      </header>

      <Banner
        v-if="settings && !isConfigured"
        color="amber"
        :action-label="t('LOCO.AI.ASSISTANTS.SET_UP')"
        @action="openSettings"
      >
        {{ t('LOCO.AI.ASSISTANTS.NOT_CONFIGURED') }}
      </Banner>

      <div
        v-if="assistants && !assistants.length"
        class="flex flex-col items-center gap-3 py-12 text-center"
      >
        <Icon icon="i-lucide-bot" class="size-10 text-n-slate-9" />
        <p class="text-sm text-n-slate-11 max-w-sm">
          {{ t('LOCO.AI.ASSISTANTS.EMPTY') }}
        </p>
      </div>

      <ul v-else-if="assistants" class="flex flex-col gap-3 list-none">
        <li v-for="assistant in assistants" :key="assistant.id">
          <button
            type="button"
            class="w-full flex items-start gap-4 p-4 rounded-xl text-start outline outline-1 -outline-offset-1 outline-n-weak hover:bg-n-alpha-1"
            @click="openAssistant(assistant.id)"
          >
            <span
              class="size-10 flex-shrink-0 rounded-full flex items-center justify-center bg-n-iris-3 text-n-iris-11"
            >
              <Icon icon="i-lucide-sparkles" class="size-5" />
            </span>
            <span class="flex flex-col gap-1 min-w-0 flex-1">
              <span class="flex items-center gap-2">
                <span class="font-medium text-n-slate-12 truncate">
                  {{ assistant.name }}
                </span>
                <span
                  class="px-1.5 rounded text-xs"
                  :class="
                    assistant.active
                      ? 'bg-n-teal-3 text-n-teal-11'
                      : 'bg-n-slate-3 text-n-slate-11'
                  "
                >
                  {{
                    assistant.active
                      ? t('LOCO.AI.ASSISTANTS.ACTIVE')
                      : t('LOCO.AI.ASSISTANTS.OFF')
                  }}
                </span>
              </span>
              <span
                v-if="assistant.description"
                class="text-sm text-n-slate-11 line-clamp-2"
              >
                {{ assistant.description }}
              </span>
              <span class="flex flex-wrap gap-1 mt-1">
                <span
                  v-for="inboxId in assistant.inbox_ids"
                  :key="inboxId"
                  class="px-2 py-0.5 rounded-full text-xs bg-n-alpha-2 text-n-slate-12"
                >
                  {{ inboxNames[inboxId] || inboxId }}
                </span>
                <span
                  v-if="!assistant.inbox_ids.length"
                  class="text-xs text-n-slate-10"
                >
                  {{ t('LOCO.AI.ASSISTANTS.NO_INBOXES') }}
                </span>
              </span>
            </span>
            <span class="flex flex-col items-end gap-1 text-xs text-n-slate-11">
              <span>
                {{ t('LOCO.AI.ASSISTANTS.SOURCES', assistant.sources_count) }}
              </span>
              <span>
                {{ t('LOCO.AI.ASSISTANTS.TOOLS', assistant.tools_count) }}
              </span>
              <span v-if="assistant.suggestions_count" class="text-n-amber-11">
                {{
                  t(
                    'LOCO.AI.ASSISTANTS.SUGGESTIONS',
                    assistant.suggestions_count
                  )
                }}
              </span>
            </span>
          </button>
        </li>
      </ul>
    </div>

    <Dialog
      ref="dialogRef"
      :title="t('LOCO.AI.ASSISTANTS.NEW')"
      :confirm-button-label="t('LOCO.AI.ASSISTANTS.CREATE')"
      :is-loading="isCreating"
      :disable-confirm-button="!draft.name.trim()"
      @confirm="create"
    >
      <div class="flex flex-col gap-4">
        <label class="flex flex-col gap-1 text-sm">
          <span class="font-medium text-n-slate-12">
            {{ t('LOCO.AI.SETUP.NAME') }}
          </span>
          <input
            v-model="draft.name"
            type="text"
            maxlength="80"
            class="!mb-0"
            :placeholder="t('LOCO.AI.SETUP.NAME_PLACEHOLDER')"
          />
        </label>
        <label class="flex flex-col gap-1 text-sm">
          <span class="font-medium text-n-slate-12">
            {{ t('LOCO.AI.SETUP.DESCRIPTION') }}
          </span>
          <input
            v-model="draft.description"
            type="text"
            class="!mb-0"
            :placeholder="t('LOCO.AI.SETUP.DESCRIPTION_PLACEHOLDER')"
          />
        </label>
      </div>
    </Dialog>
  </div>
</template>
